import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject || "General enquiry",
        message: form.message,
      });
      setSent(true);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="contact-page">
      <div className="section-dark px-5 py-16 sm:px-8">
        <div className="mx-auto max-w-[1400px]">
          <p className="mb-3 font-mono text-xs uppercase tracking-overline text-[#34D8A0]">We're Here To Help</p>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter sm:text-6xl">Contact Us</h1>
          <p className="mt-4 max-w-lg text-white/60">
            Questions about an order, a formula, or which supplement fits your recovery? Send us a message.
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
        {/* Info */}
        <div className="space-y-8">
          {[
            { icon: Mail, label: "Email", value: "care@physioessential.fit" },
            { icon: Phone, label: "Phone / UPI", value: "+91 78386 87165" },
            { icon: MapPin, label: "Brand", value: "Physio Essential · Gd Global Health" },
          ].map((c) => (
            <div key={c.label} className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2F80ED]/10 text-[#2F80ED]">
                <c.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-overline text-black/40">{c.label}</div>
                <div className="mt-1 font-medium">{c.value}</div>
              </div>
            </div>
          ))}
          <div className="border border-black/10 bg-[#F3F4F6] p-6 text-sm leading-relaxed text-black/60">
            Tracking an order? Log in and open <span className="font-semibold text-black">My Orders</span> to see live
            status, or mention your order ID in the message.
          </div>
        </div>

        {/* Form */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border border-black/10 bg-white p-8"
          data-testid="contact-form"
        >
          {sent && (
            <div className="mb-6 border border-[#34D8A0] bg-[#34D8A0]/10 px-4 py-3 text-sm font-medium text-[#0A2540]" data-testid="contact-success">
              Thanks! Your message has been received — we'll reply to your email soon.
            </div>
          )}
          <div className="grid grid-cols-2 gap-5">
            <div className="col-span-2 sm:col-span-1">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Name</Label>
              <Input value={form.name} onChange={upd("name")} className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]" data-testid="contact-name" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Email</Label>
              <Input type="email" value={form.email} onChange={upd("email")} className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]" data-testid="contact-email" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Phone (optional)</Label>
              <Input value={form.phone} onChange={upd("phone")} className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]" data-testid="contact-phone" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Subject</Label>
              <Input value={form.subject} onChange={upd("subject")} placeholder="Order query, product help…" className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]" data-testid="contact-subject" />
            </div>
            <div className="col-span-2">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-overline text-black/50">Message</Label>
              <Textarea value={form.message} onChange={upd("message")} rows={5} className="rounded-none border-black/15 focus-visible:ring-[#2F80ED]" data-testid="contact-message" />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-glow mt-6 flex items-center justify-center gap-2 bg-black px-8 py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED] disabled:opacity-60"
            data-testid="contact-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send Message
          </button>
        </motion.form>
      </div>
    </div>
  );
}
